"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { compressImageToWebP } from "@/lib/utils/image";
import {
  SUPPORT_IMAGE_MAX_BYTES,
  SUPPORT_IMAGE_MAX_FILES,
  SUPPORT_IMAGE_MAX_MB,
  SUPPORT_IMAGE_TYPES,
} from "@/lib/chat/support-attachments";
import type { MessageAttachment } from "@/types/message";

/** Vercel 함수 요청 본문 상한(4.5MB)보다 조금 낮게 — 줄인 뒤에도 넘으면 올리지 않는다. */
const UPLOAD_BYTES_LIMIT = 4 * 1024 * 1024;
const COMPRESS_OVER_BYTES = 1024 * 1024;

export interface SupportImageDraftItem {
  id: string;
  file: File;
  previewUrl: string;
  /** 이미 올린 사진 — 발송 실패 후 다시 보낼 때 또 올리지 않는다. */
  uploaded?: MessageAttachment;
}

/** 플랫폼 문의 입력창의 보내기 전 사진 목록과 업로드. */
export function useSupportImageDraft() {
  const t = useTranslations("support.composer");
  const [items, setItems] = useState<SupportImageDraftItem[]>([]);
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // 화면을 떠날 때 미리보기 주소를 돌려준다.
  useEffect(
    () => () => itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl)),
    [],
  );

  const add = useCallback(
    (files: Iterable<File>) => {
      const next: SupportImageDraftItem[] = [];
      for (const file of files) {
        if (!SUPPORT_IMAGE_TYPES.includes(file.type)) {
          toast.error(t("imageTypeInvalid"));
          continue;
        }
        if (file.size > SUPPORT_IMAGE_MAX_BYTES) {
          toast.error(t("imageTooLarge", { max: SUPPORT_IMAGE_MAX_MB }));
          continue;
        }
        if (itemsRef.current.length + next.length >= SUPPORT_IMAGE_MAX_FILES) {
          toast.error(t("imageTooMany", { max: SUPPORT_IMAGE_MAX_FILES }));
          break;
        }
        next.push({ id: crypto.randomUUID(), file, previewUrl: URL.createObjectURL(file) });
      }
      if (next.length === 0) return;
      itemsRef.current = [...itemsRef.current, ...next];
      setItems(itemsRef.current);
    },
    [t],
  );

  const remove = useCallback((id: string) => {
    const target = itemsRef.current.find((item) => item.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    itemsRef.current = itemsRef.current.filter((item) => item.id !== id);
    setItems(itemsRef.current);
  }, []);

  const clear = useCallback(() => {
    itemsRef.current.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    itemsRef.current = [];
    setItems([]);
  }, []);

  /** 전부 올린다. 하나라도 실패하면 안내하고 null — 글과 사진은 입력창에 그대로 남는다. */
  const uploadAll = useCallback(
    async (conversationId: string): Promise<MessageAttachment[] | null> => {
      try {
        const attachments = await Promise.all(
          itemsRef.current.map(async (item) => {
            if (item.uploaded) return item.uploaded;

            let toUpload = item.file;
            if (toUpload.type !== "image/gif" && toUpload.size > COMPRESS_OVER_BYTES) {
              try {
                toUpload = await compressImageToWebP(toUpload, { maxLongEdge: 2560, quality: 0.9 });
              } catch (err) {
                console.error("Support image compression failed:", err);
              }
            }
            if (toUpload.size > UPLOAD_BYTES_LIMIT) {
              throw new Error("IMAGE_TOO_LARGE");
            }

            const formData = new FormData();
            formData.append("file", toUpload);
            formData.append("conversationId", conversationId);
            const response = await fetch("/api/upload-support-attachment", {
              method: "POST",
              body: formData,
            });
            if (!response.ok) throw new Error(`upload failed: ${response.status}`);
            const { attachment } = (await response.json()) as { attachment: MessageAttachment };
            item.uploaded = attachment;
            return attachment;
          }),
        );
        return attachments;
      } catch (err) {
        console.error("Support image upload failed:", err);
        toast.error(
          err instanceof Error && err.message === "IMAGE_TOO_LARGE"
            ? t("imageTooLarge", { max: SUPPORT_IMAGE_MAX_MB })
            : t("imageUploadFailed"),
        );
        return null;
      }
    },
    [t],
  );

  return { items, add, remove, clear, uploadAll };
}
